/**
 * Drop-in replacement for fetch() that adds automatic retry with exponential
 * backoff for Google Drive API rate limits (429) and server errors (500-503).
 *
 * Same signature as fetch(). Does NOT retry on 401/403/404/400.
 * Backoff: 1s → 2s → 4s + random jitter (0-500ms).
 * Respects Retry-After header when present.
 * Logs each retry (INFO) and final failure (WARNING to Sentry).
 */

import { logger } from '../../utils/logger';

const MAX_ATTEMPTS = 3;
const BASE_DELAY_MS = 1000;
const MAX_JITTER_MS = 500;

const RETRYABLE_STATUSES = new Set([429, 500, 502, 503]);

export async function driveFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  let lastResponse: Response | undefined;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const response = await fetch(input, init);

    if (!RETRYABLE_STATUSES.has(response.status)) {
      return response;
    }

    lastResponse = response;

    // Don't wait after the last attempt
    if (attempt < MAX_ATTEMPTS - 1) {
      const retryAfter = parseRetryAfter(response);
      const backoff = retryAfter ?? BASE_DELAY_MS * Math.pow(2, attempt);
      const jitter = Math.random() * MAX_JITTER_MS;
      const delay = backoff + jitter;

      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      logger.info('driveFetch', `Retry ${attempt + 1}/${MAX_ATTEMPTS - 1} after ${response.status} — waiting ${Math.round(delay)}ms`, { url: truncateUrl(url) });

      await sleep(delay);
    }
  }

  // All attempts exhausted — log to Sentry and return last response
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  logger.error('driveFetch', `All ${MAX_ATTEMPTS} attempts failed with ${lastResponse!.status}`, { url: truncateUrl(url), status: lastResponse!.status });

  return lastResponse!;
}

function parseRetryAfter(response: Response): number | null {
  const header = response.headers.get('Retry-After');
  if (!header) return null;
  const seconds = Number(header);
  if (!Number.isNaN(seconds) && seconds > 0 && seconds <= 120) {
    return seconds * 1000;
  }
  return null;
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Thrown when a save receives HTTP 412 Precondition Failed (ETag mismatch).
 * Stores should catch this specifically to reload + toast.
 */
export class DriveConflictError extends Error {
  constructor(public readonly fileName: string) {
    super(`Conflito de versão em ${fileName}`);
    this.name = 'DriveConflictError';
  }
}

function truncateUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname.slice(0, 80);
  } catch {
    return url.slice(0, 80);
  }
}
